import React from "react"
import { Button } from "@material-tailwind/react"
import { Bars3Icon } from "@heroicons/react/24/outline"
import { Link, useLocation } from "react-router-dom"
import { useDispatch, useSelector } from "react-redux"
import { logout } from "../../actions/userActions"
import { BsFillBoxSeamFill } from "react-icons/bs"
import { RiAddBoxFill } from "react-icons/ri"
import { LuTruck } from "react-icons/lu"
import { HiOutlineLocationMarker, HiOutlineUserAdd } from "react-icons/hi"
import { FaWarehouse, FaList } from "react-icons/fa6"
import { MdSwitchAccount } from "react-icons/md"
import { IoPersonAdd, IoLogOutOutline } from "react-icons/io5"
import { FiArrowRight, FiBox, FiHome, FiSettings } from "react-icons/fi"
import { TbTruckDelivery } from "react-icons/tb"

const SidebarItem = ({ to, icon: Icon, label, isActive, isCollapsed, onClick }) => (
  <Link to={to} onClick={onClick} className="block">
    <div
      className={`group relative flex items-center rounded-[18px] transition-all duration-200 ${
        isActive
          ? "bg-[linear-gradient(135deg,_rgba(255,111,47,0.16),_rgba(255,151,110,0.08))] text-[#f97316] shadow-[inset_0_0_0_1px_rgba(251,146,60,0.18)]"
          : "text-slate-600 hover:bg-[#fff7f2] hover:text-slate-900"
      } ${isCollapsed ? "justify-center px-0 py-3" : "gap-3 px-4 py-3"}`}
      title={isCollapsed ? label : ""}
    >
      <div
        className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-2xl transition-colors ${
          isActive
            ? "bg-white text-[#f97316] shadow-[0_10px_22px_rgba(249,115,22,0.12)]"
            : "text-slate-500 group-hover:bg-white group-hover:text-slate-700"
        }`}
      >
        <Icon className="h-4.5 w-4.5 shrink-0" />
      </div>
      {!isCollapsed && <span className="truncate text-[0.94rem] font-[GilroyMedium]">{label}</span>}
    </div>
  </Link>
)

export function Sidebar() {
  const { userProfile } = useSelector((state) => state.user)
  const isDeliveryUser = ['Delivery Boy', 'Delivery Partner'].includes(userProfile?.role)
  const [isMobileOpen, setIsMobileOpen] = React.useState(false)
  const [isDesktopHovered, setIsDesktopHovered] = React.useState(false)
  const dispatch = useDispatch()
  const { pathname } = useLocation()

  React.useEffect(() => {
    const handleOpen = () => setIsMobileOpen(true)
    const handleClose = () => setIsMobileOpen(false)

    window.addEventListener("grc:open-sidebar", handleOpen)
    window.addEventListener("grc:close-sidebar", handleClose)

    return () => {
      window.removeEventListener("grc:open-sidebar", handleOpen)
      window.removeEventListener("grc:close-sidebar", handleClose)
    }
  }, [])

  React.useEffect(() => {
    setIsMobileOpen(false)
  }, [pathname])

  const handleLogout = () => {
    dispatch(logout())
  }

  const closeMobileSidebar = () => {
    setIsMobileOpen(false)
  }

  const primaryItems = [
    { to: "/", label: "Overview", icon: FiHome, match: (path) => path === "/" },
    ...(isDeliveryUser
      ? [
          { to: "/pickupList", label: "Pickup Request", icon: HiOutlineLocationMarker, match: (path) => path.startsWith("/pickupList") },
          { to: "/deliveryList", label: "Assigned Deliveries", icon: TbTruckDelivery, match: (path) => path.startsWith("/deliveryList") },
          { to: "/servicibility", label: "Serviceability", icon: LuTruck, match: (path) => path.startsWith("/servicibility") },
          { to: "/createDelivery", label: "Create Order", icon: RiAddBoxFill, match: (path) => path.startsWith("/createDelivery") },
        ]
      : [
    { to: "/partnerlist", label: "Delivery Run Sheet", icon: FiBox, match: (path) => path.startsWith("/partnerlist") },
    { to: "/orders", label: "Orders", icon: BsFillBoxSeamFill, match: (path) => path.startsWith("/orders") || path.startsWith("/orderDetails") },
    ...(userProfile?.role === "admin"
      ? [
          { to: "/create-order", label: "Create Order", icon: RiAddBoxFill, match: (path) => path.startsWith("/create-order") },
          { to: "/request-pickup", label: "Pickup Request", icon: HiOutlineLocationMarker, match: (path) => path.startsWith("/request-pickup") },
          { to: "/servicibility", label: "Serviceability", icon: LuTruck, match: (path) => path.startsWith("/servicibility") },
          { to: "/manageDelivery", label: "Drivers", icon: IoPersonAdd, match: (path) => path.startsWith("/manageDelivery") },
          { to: "/warehouse", label: "Warehouses", icon: FaWarehouse, match: (path) => path.startsWith("/warehouse") || path.startsWith("/editwarehouse") },
          { to: "/users", label: "Customers", icon: FaList, match: (path) => path.startsWith("/users") },
          { to: "/DeliveryBoyDataList", label: "Reports", icon: FaList, match: (path) => path.startsWith("/DeliveryBoyDataList") },
          { to: "/createBranch", label: "Settings", icon: FiSettings, match: (path) => path.startsWith("/createBranch") },
        ]
      : [
          { to: "/request-pickup", label: "Pickup Request", icon: HiOutlineLocationMarker, match: (path) => path.startsWith("/request-pickup") },
          { to: "/servicibility", label: "Serviceability", icon: LuTruck, match: (path) => path.startsWith("/servicibility") },
        ]),
      ]),
  ]

  const secondaryItems = [
    ...(!isDeliveryUser && userProfile?.role === "admin"
      ? [
          { to: "/create-user", label: "Create User", icon: HiOutlineUserAdd, match: (path) => path.startsWith("/create-user") },
          { to: "/create-staff", label: "Create Staff", icon: HiOutlineUserAdd, match: (path) => path.startsWith("/create-staff") },
        ]
      : []),
    { to: "/profile", label: "Account", icon: MdSwitchAccount, match: (path) => path.startsWith("/profile") },
  ]

  const profileName = userProfile?.company_name || userProfile?.name || "Admin User"
  const profileRole = userProfile?.role || "User"
  const profileInitial = profileName.charAt(0).toUpperCase()

  const renderSidebarContent = (isCollapsed, isDesktop = false) => (
    <>
      <div className={`px-4 ${isCollapsed ? "pt-5 pb-4" : "pt-6 pb-5"}`}>
        <Link to="/" className={`flex items-center ${isCollapsed ? "justify-center" : "gap-3"}`}>
          <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-[linear-gradient(135deg,_#ff6b2c,_#ff8d57)] text-white shadow-[0_14px_28px_rgba(249,115,22,0.22)]">
            <FiBox className="h-5 w-5" />
          </div>
          {!isCollapsed && (
            <div className="min-w-0">
              <p className="truncate text-[1.2rem] font-[GilroyBold] tracking-[-0.04em] text-slate-950">GRC</p>
              <p className="mt-1 text-[0.74rem] font-[GilroyMedium] text-slate-400">Operations workspace</p>
            </div>
          )}
        </Link>
      </div>

      <div className="flex-1 overflow-y-auto px-3 pb-4 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
        <div className="space-y-1.5">
          {primaryItems.map((item) => (
            <SidebarItem
              key={item.to}
              to={item.to}
              icon={item.icon}
              label={item.label}
              isCollapsed={isCollapsed}
              isActive={item.match(pathname)}
              onClick={closeMobileSidebar}
            />
          ))}
        </div>

        {secondaryItems.length > 0 && (
          <div className="mt-6 space-y-1.5 border-t border-[rgba(15,23,42,0.06)] pt-5">
            {secondaryItems.map((item) => (
              <SidebarItem
                key={item.to}
                to={item.to}
                icon={item.icon}
                label={item.label}
                isCollapsed={isCollapsed}
                isActive={item.match(pathname)}
                onClick={closeMobileSidebar}
              />
            ))}

            <div
              onClick={handleLogout}
              className={`group relative flex cursor-pointer items-center rounded-[18px] text-slate-600 transition-all duration-200 hover:bg-[#fff7f2] hover:text-slate-900 ${
                isCollapsed ? "justify-center px-0 py-3" : "gap-3 px-4 py-3"
              }`}
              title={isCollapsed ? "Logout" : ""}
            >
              <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-2xl text-slate-500 group-hover:bg-white group-hover:text-slate-700">
                <IoLogOutOutline className="h-4.5 w-4.5" />
              </div>
              {!isCollapsed && <span className="truncate text-[0.94rem] font-[GilroyMedium]">Logout</span>}
            </div>
          </div>
        )}
      </div>

      <div className="border-t border-[rgba(15,23,42,0.06)] px-4 py-4">
        <div
          className={`rounded-[22px] border border-[rgba(15,23,42,0.06)] bg-white/90 shadow-[0_12px_26px_rgba(15,23,42,0.05)] ${
            isCollapsed ? "flex justify-center p-3" : "flex items-center gap-3 px-3 py-3"
          }`}
        >
          <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-[linear-gradient(135deg,_#0f172a,_#334155)] text-sm font-[GilroyBold] text-white">
            {profileInitial}
          </div>
          {!isCollapsed && (
            <>
              <div className="min-w-0 flex-1">
                <p className="truncate text-[0.95rem] font-[GilroyBold] text-slate-900">{profileName}</p>
                <p className="truncate text-xs text-slate-400">{profileRole}</p>
              </div>
              {isDesktop && <FiArrowRight className="h-4 w-4 rotate-90 text-slate-400" />}
            </>
          )}
        </div>
      </div>
    </>
  )

  return (
    <>
      {isMobileOpen && (
        <div className="fixed inset-0 z-40 bg-slate-950/45 backdrop-blur-[2px] lg:hidden" onClick={closeMobileSidebar} />
      )}

      <aside
        className={`fixed inset-y-3 left-3 z-50 flex w-[280px] flex-col rounded-[30px] border border-white/70 bg-[linear-gradient(180deg,_rgba(255,255,255,0.98),_rgba(250,250,250,0.94))] shadow-[0_28px_70px_rgba(15,23,42,0.18)] backdrop-blur-xl transition-transform duration-300 lg:hidden ${
          isMobileOpen ? "translate-x-0" : "-translate-x-[calc(100%+1.5rem)]"
        }`}
      >
        <div className="border-b border-[rgba(15,23,42,0.06)] px-3 py-3">
          <Button
            variant="text"
            onClick={closeMobileSidebar}
            className="w-full rounded-2xl border border-slate-200/80 bg-white/70 p-2.5 text-slate-500 shadow-none hover:bg-white"
          >
            <Bars3Icon className="mx-auto h-4 w-4" />
          </Button>
        </div>
        {renderSidebarContent(false)}
      </aside>

      <aside
        onMouseEnter={() => setIsDesktopHovered(true)}
        onMouseLeave={() => setIsDesktopHovered(false)}
        className={`relative hidden h-[calc(100vh-1.5rem)] shrink-0 flex-col rounded-[30px] border border-[rgba(15,23,42,0.06)] bg-[linear-gradient(180deg,_rgba(255,255,255,0.98),_rgba(250,250,250,0.94))] shadow-[0_28px_70px_rgba(15,23,42,0.10)] backdrop-blur-xl transition-all duration-300 lg:sticky lg:top-3 lg:flex ${
          isDesktopHovered ? "w-[286px]" : "w-[92px]"
        }`}
      >
        {renderSidebarContent(!isDesktopHovered, true)}
      </aside>
    </>
  )
}
